import { ArrowDown, ArrowUp, ChevronLeft, ChevronRight, IdCard } from 'lucide-react';
import type { WorkspaceUserListItem } from '../../hooks/useUsersList';
import type { UsersSort, UsersSortBy } from '../../lib/users-list';
import { cn } from '../../lib/utils';
import { Badge } from '../../ui/badge';
import { Button } from '../../ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../ui/table';
import { UserAvatar } from '../../ui/user-avatar';

export interface UsersTableProps {
  items: WorkspaceUserListItem[];
  isLoading: boolean;
  sort: UsersSort;
  onToggleSort: (by: UsersSortBy) => void;
  /** Marks the signed-in user's own row with a "You" badge. */
  currentUserId?: number;
  emptyLabel?: string;
  pageStart: number;
  pageEnd: number;
  totalCount: number;
  hasNextPage: boolean;
  hasPrevPage: boolean;
  onNextPage: () => void;
  onPrevPage: () => void;
}

const COLUMN_COUNT = 6;

function SortableHead({
  field,
  label,
  sort,
  onToggleSort,
  className,
}: {
  field: UsersSortBy;
  label: string;
  sort: UsersSort;
  onToggleSort: (by: UsersSortBy) => void;
  className?: string;
}) {
  const isActive = sort.by === field;
  const Arrow = sort.dir === 'asc' ? ArrowUp : ArrowDown;
  return (
    // aria-sort belongs on the columnheader itself, not the button inside it.
    <TableHead
      className={className}
      aria-sort={isActive ? (sort.dir === 'asc' ? 'ascending' : 'descending') : 'none'}
    >
      <button
        type="button"
        onClick={() => onToggleSort(field)}
        className={cn(
          'flex items-center gap-1 -mx-1 px-1 py-0.5 rounded transition-colors hover:text-foreground',
          isActive ? 'text-foreground font-medium' : 'text-muted-foreground'
        )}
      >
        {label}
        <Arrow
          className={cn('h-3 w-3 transition-opacity', isActive ? 'opacity-100' : 'opacity-0')}
        />
      </button>
    </TableHead>
  );
}

/**
 * Presentational workspace-member table — the Contacts table's layout and
 * pagination footer, minus every affordance that mutates. Rows are inert: a user's
 * profile belongs to that user, and role/membership changes live in Settings
 * behind an admin check, so there is nothing here to click into or edit.
 */
export function UsersTable({
  items,
  isLoading,
  sort,
  onToggleSort,
  currentUserId,
  emptyLabel = 'No users found',
  pageStart,
  pageEnd,
  totalCount,
  hasNextPage,
  hasPrevPage,
  onNextPage,
  onPrevPage,
}: UsersTableProps) {
  return (
    <div className="rounded-xl border border-border/50 bg-card/50 backdrop-blur-sm overflow-hidden">
      <Table>
        <TableHeader>
          <TableRow className="hover:bg-transparent">
            <SortableHead
              field="fullName"
              label="Name"
              sort={sort}
              onToggleSort={onToggleSort}
              className="w-[260px]"
            />
            <SortableHead field="email" label="Email" sort={sort} onToggleSort={onToggleSort} />
            <SortableHead
              field="jobTitle"
              label="Job Title"
              sort={sort}
              onToggleSort={onToggleSort}
            />
            <TableHead>Phone</TableHead>
            <SortableHead field="role" label="Role" sort={sort} onToggleSort={onToggleSort} />
            <SortableHead field="joinedAt" label="Joined" sort={sort} onToggleSort={onToggleSort} />
          </TableRow>
        </TableHeader>
        <TableBody>
          {isLoading ? (
            Array(5)
              .fill(0)
              .map((_, i) => (
                <TableRow key={i}>
                  {Array(COLUMN_COUNT)
                    .fill(0)
                    .map((__, j) => (
                      <TableCell key={j}>
                        <div className="h-4 w-24 bg-muted animate-pulse rounded" />
                      </TableCell>
                    ))}
                </TableRow>
              ))
          ) : items.length === 0 ? (
            <TableRow>
              <TableCell colSpan={COLUMN_COUNT} className="h-64 text-center">
                <div className="flex flex-col items-center justify-center space-y-2">
                  <IdCard className="h-10 w-10 text-muted-foreground opacity-20" />
                  <p className="text-sm text-muted-foreground">{emptyLabel}</p>
                </div>
              </TableCell>
            </TableRow>
          ) : (
            items.map((user) => (
              <TableRow key={user.id} className="hover:bg-transparent">
                <TableCell className="font-medium">
                  <div className="flex items-center gap-2.5 min-w-0">
                    <UserAvatar user={user} size="sm" />
                    <span className="truncate">{user.fullName || user.email}</span>
                    {user.id === currentUserId && (
                      <Badge variant="outline" className="font-normal text-[10px] px-1.5 shrink-0">
                        You
                      </Badge>
                    )}
                  </div>
                </TableCell>
                <TableCell className="max-w-[220px]">
                  <span className="text-xs text-muted-foreground truncate block">{user.email}</span>
                </TableCell>
                <TableCell className="max-w-[180px]">
                  <span className="text-xs text-muted-foreground truncate block">
                    {user.jobTitle || '—'}
                  </span>
                </TableCell>
                <TableCell className="max-w-[140px]">
                  <span className="text-xs text-muted-foreground truncate block">
                    {user.phoneNumber || '—'}
                  </span>
                </TableCell>
                <TableCell>
                  <Badge variant="secondary" className="font-normal capitalize">
                    {user.role || 'viewer'}
                  </Badge>
                </TableCell>
                <TableCell className="text-xs text-muted-foreground">
                  {new Date(user.joinedAt * 1000).toLocaleDateString()}
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>

      {!isLoading && totalCount > 0 && (
        <div className="flex items-center justify-between px-4 py-3 border-t border-border/50">
          <span className="text-xs text-muted-foreground">
            {pageStart}–{pageEnd} of {totalCount}
          </span>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="icon"
              className="h-7 w-7"
              disabled={!hasPrevPage}
              onClick={onPrevPage}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              size="icon"
              className="h-7 w-7"
              disabled={!hasNextPage}
              onClick={onNextPage}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
